<template>
  <NModal class="max-w-[800px] rounded-2" :on-esc="onClose" :on-update:show="onClose" show>
    <NCard class="rounded-2">
      <template #header>
        <div class="text-lg font-bold">
          {{ t('canvasUI.smartFolders.smartFoldersConfigurationModal.configureSmartFolders') }}
        </div>
      </template>
      <div class="flex flex-col h-[60vh]">
        <NSpin v-if="isLoadingConfig" class="flex-grow flex items-center justify-center" />
        <div v-else class="flex flex-col h-full">
          <div class="flex-grow overflow-auto">
            <div class="font-500 tracking-[1.25px] my-4">
              {{ t('canvasUI.smartFolders.smartFoldersConfigurationModal.categorizeBasedOn') }}
            </div>
            <VueDraggable v-model="enabledPropertyNames" handle=".drag-handle">
              <div
                v-for="name in enabledPropertyNames"
                :key="name"
                class="flex flex-col justify-center min-h-[40px] color-text2 mb-2"
              >
                <div class="flex items-center">
                  <CIcon class="drag-handle mr-2 cursor-move" icon="grip-dots-vertical" size="20" />
                  <div class="flex-grow border border-solid border-primary4 border-rounded-md pa-2 bg-base">
                    <div class="flex items-center justify-between">
                      <div>
                        <div class="font-500 color-primary">
                          {{ getPropertyDisplayName(name) }}
                        </div>
                      </div>
                      <CButton circle :disabled="isSavingConfig" icon @click="disableSmartFolderProperty(name)">
                        <CIcon icon="minus" size="16px" />
                      </CButton>
                    </div>
                  </div>
                </div>
              </div>
            </VueDraggable>
            <div v-if="!enabledPropertyNames.length" class="text-center color-text3">
              {{ t('canvasUI.smartFolders.smartFoldersConfigurationModal.noPropertiesSelected') }}
            </div>

            <div class="font-500 tracking-[1.25px] my-4">
              {{ t('canvasUI.smartFolders.smartFoldersConfigurationModal.availableProperties') }}
            </div>
            <div class="flex flex-col gap-2">
              <div
                v-for="name in disabledPropertyNames"
                :key="name"
                class="flex flex-col justify-center min-h-[40px] color-text2"
              >
                <div
                  class="flex items-center justify-between border border-solid border-primary4 border-rounded-md pa-2"
                >
                  <div>
                    <div class="font-500">
                      {{ getPropertyDisplayName(name) }}
                    </div>
                  </div>
                  <CButton
                    circle
                    :disabled="isSavingConfig"
                    icon
                    type="primary"
                    @click="enableSmartFolderProperty(name)"
                  >
                    <CIcon icon="plus" size="16px" />
                  </CButton>
                </div>
              </div>
            </div>
            <div v-if="!disabledPropertyNames.length" class="text-center py-4 color-text3">
              {{ t('canvasUI.smartFolders.smartFoldersConfigurationModal.noPropertiesToAdd') }}
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-between gap-4">
          <CButton :disabled="isSavingConfig" @click="onClose">
            {{ t('canvasUI.common.cancel') }}
          </CButton>
          <CButton :loading="isSavingConfig" type="primary" @click="saveChanges">
            {{ t('canvasUI.common.save') }}
          </CButton>
        </div>
      </template>
    </NCard>
  </NModal>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { VueDraggable } from 'vue-draggable-plus'
import { NCard, NModal, NSpin } from 'naive-ui'

import CIcon from '@lib/components/CIcon/CIcon.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import useSmartFolders, { type SmartFolderProperty } from './SmartFolders.use'

const { t } = useI18n()

const emit = defineEmits<{
  close: []
}>()

const { activeConfiguration, isLoadingConfig, isSavingConfig, saveConfiguration, allSmartFolderProperties } =
  useSmartFolders()

const enabledPropertyNames = ref<SmartFolderProperty['name'][]>([
  ...(activeConfiguration.value?.enabledSmartFolderProperties || []),
])

const disabledPropertyNames = computed(() =>
  allSmartFolderProperties.value.map((prop) => prop.name).filter((name) => !enabledPropertyNames.value.includes(name)),
)

function disableSmartFolderProperty(name: SmartFolderProperty['name']) {
  enabledPropertyNames.value = enabledPropertyNames.value.filter((prop) => prop !== name)
}

function enableSmartFolderProperty(name: SmartFolderProperty['name']) {
  enabledPropertyNames.value.push(name)
}

function getPropertyDisplayName(name: SmartFolderProperty['name']) {
  const property = allSmartFolderProperties.value.find((prop) => prop.name === name)
  return property?.display_name || name
}

function onClose() {
  if (isSavingConfig.value) return
  emit('close')
}

async function saveChanges() {
  await saveConfiguration({
    enabledSmartFolderProperties: enabledPropertyNames.value,
  })
  emit('close')
}
</script>
